<template>
  <div class="w-screen h-screen overflow-hidden" :style="getBackgroundStyle()">
    <!-- Logo -->
    <div v-if="config.branding?.logo?.show && config.branding?.logo?.url"
         :class="['absolute z-50', getLogoPositionClass()]"
         class="p-6">
      <img :src="config.branding.logo.url" :style="{ width: config.branding.logo.size + 'px', height: 'auto' }" />
    </div>

    <!-- Zones -->
    <div class="w-full h-full p-8"
         :class="config.layout?.type === 'custom' ? '' : 'flex ' + getLayoutClass()"
         :style="{ gap: config.layout?.gap + 'px' }">
      <div v-for="zone in config.zones" :key="zone.id"
           class="relative overflow-hidden"
           :style="getZoneStyle(zone)">

        <!-- Zone Content -->
        <div class="w-full h-full" :style="{ padding: zone.style?.padding + 'px' }">
          <!-- Queue Display -->
          <div v-if="zone.content.type === 'queue_display'" class="h-full flex flex-col">
            <h2 class="text-2xl font-bold mb-4" :style="getTextStyle(zone)">Files d'attente</h2>
            <div class="flex-1 overflow-auto space-y-3">
              <div v-if="getTicketsForZone(zone).length === 0" class="text-center text-slate-400 mt-8">
                Aucun ticket à afficher
              </div>
              <div v-for="ticket in getTicketsForZone(zone)" :key="ticket._id"
                   class="flex justify-between items-center p-4 rounded-xl shadow-sm transition-all"
                   :style="{ backgroundColor: config.branding?.colors?.primary + '10', borderLeft: '4px solid ' + config.branding?.colors?.primary }">
                <div>
                  <span class="text-3xl font-black" :style="{ color: config.branding?.colors?.primary }">
                    {{ ticket.displayNumber }}
                  </span>
                  <span class="text-sm text-slate-500 ml-3">{{ getQueueName(ticket.queueId) }}</span>
                </div>
                <div class="text-right">
                  <div class="text-xl font-bold" :style="{ color: config.branding?.colors?.text }">
                    {{ ticket.status === 'WAITING' ? 'En attente' : `Guichet ${ticket.counterNumber || '-'}` }}
                  </div>
                  <div class="text-xs text-slate-400">
                    {{ ticket.status === 'WAITING' ? 'File d\'attente' : 'Appelé' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Video -->
          <div v-else-if="zone.content.type === 'video'" class="w-full h-full">
            <iframe v-if="zone.content.videoUrl"
                    :src="zone.content.videoUrl"
                    class="w-full h-full"
                    frameborder="0"
                    :allow="zone.content.autoplay ? 'autoplay' : ''"
                    allowfullscreen>
            </iframe>
          </div>

          <!-- Image -->
          <div v-else-if="zone.content.type === 'image'" class="w-full h-full">
            <img v-if="zone.content.imageUrl"
                 :src="zone.content.imageUrl"
                 class="w-full h-full"
                 :style="{ objectFit: zone.content.imageFit || 'cover' }" />
          </div>

          <!-- Text -->
          <div v-else-if="zone.content.type === 'text'" class="w-full h-full flex items-center justify-center">
            <div :style="getTextStyle(zone)">{{ zone.content.text }}</div>
          </div>

          <!-- Clock -->
          <div v-else-if="zone.content.type === 'clock'" class="w-full h-full flex flex-col items-center justify-center">
            <div class="text-7xl font-bold" :style="{ color: zone.textStyle?.color }">{{ currentTime }}</div>
            <div v-if="zone.content.showDate" class="text-2xl mt-4" :style="{ color: zone.textStyle?.color }">{{ currentDate }}</div>
          </div>

          <!-- Weather -->
          <div v-else-if="zone.content.type === 'weather'" class="w-full h-full flex items-center justify-center">
            <div class="text-center">
              <div class="text-8xl mb-4">☀️</div>
              <div class="text-5xl font-bold" :style="{ color: zone.textStyle?.color }">24°C</div>
              <div class="text-xl mt-2" :style="{ color: zone.textStyle?.color }">{{ zone.content.location || 'Paris' }}</div>
            </div>
          </div>

          <!-- Custom HTML -->
          <div v-else-if="zone.content.type === 'custom_html'" class="w-full h-full" v-html="zone.content.html"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import io from 'socket.io-client';

const route = useRoute();
const agencyId = route.params.agencyId;

const config = ref({
  layout: { type: 'single', gap: 20 },
  zones: [],
  branding: {
    backgroundColor: '#F8FAFC',
    backgroundType: 'color',
    colors: {
      primary: '#3B82F6',
      text: '#1E293B'
    }
  }
});

const currentTickets = ref([]);
const queues = ref([]);
const socket = ref(null);

const currentTime = ref(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
const currentDate = ref(new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }));

let clockInterval = null;

onMounted(() => {
  fetchConfig();
  fetchQueues();
  fetchTickets();
  setupWebSocket();

  // Update clock
  clockInterval = setInterval(() => {
    currentTime.value = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    currentDate.value = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  }, 1000);

  // Auto-refresh tickets
  setInterval(() => {
    fetchTickets();
  }, config.value.refreshInterval || 5000);
});

onUnmounted(() => {
  if (socket.value) {
    socket.value.disconnect();
  }
  if (clockInterval) {
    clearInterval(clockInterval);
  }
});

const setupWebSocket = () => {
  socket.value = io('https://api.datafuse.fr');
  socket.value.emit('join_agency', agencyId);

  socket.value.on('ticket_called', (data) => {
    if (data.agencyId === agencyId) {
      fetchTickets();
    }
  });

  socket.value.on('screen_config_update', (data) => {
    if (data.agencyId === agencyId) {
      fetchConfig();
    }
  });

  socket.value.on('queue_visibility_refresh', (data) => {
    console.log('[Socket] 📅 Nouveau jour détecté - Rafraîchissement de la visibilité des files', data);
    console.log(`[Socket] 📅 Jour actuel: ${data.currentDay} (${data.currentDayEn})`);

    // Recharger la configuration de l'écran et les tickets
    fetchConfig();
    fetchTickets();
  });
};

const fetchConfig = async () => {
  try {
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/screen-config/${agencyId}`);
    if (res.ok) {
      config.value = await res.json();
    }
  } catch (err) {
    console.error("Erreur config:", err);
  }
};

const fetchQueues = async () => {
  try {
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/queues/${agencyId}`);
    if (res.ok) {
      queues.value = await res.json();
    }
  } catch (err) {
    console.error("Erreur queues:", err);
  }
};

const fetchTickets = async () => {
  try {
    // Récupère tous les tickets CALLED et WAITING
    const [calledRes, waitingRes] = await Promise.all([
      fetch(`https://api.datafuse.fr/api/agency/tickets?agencyId=${agencyId}&status=CALLED&limit=50`),
      fetch(`https://api.datafuse.fr/api/agency/tickets?agencyId=${agencyId}&status=WAITING&limit=50`)
    ]);

    const called = calledRes.ok ? await calledRes.json() : [];
    const waiting = waitingRes.ok ? await waitingRes.json() : [];

    // Combine les tickets
    currentTickets.value = [...called, ...waiting];
  } catch (err) {
    console.error("Erreur tickets:", err);
  }
};

// Filtre les tickets pour une zone spécifique
const getTicketsForZone = (zone) => {
  if (zone.content.type !== 'queue_display') return [];

  let tickets = currentTickets.value;

  // Filtre par files d'attente
  if (zone.content.queueIds && zone.content.queueIds.length > 0) {
    tickets = tickets.filter(t => zone.content.queueIds.includes(t.queueId));
  }

  // Filtre par statut
  if (zone.content.ticketFilter === 'CALLED') {
    tickets = tickets.filter(t => t.status === 'CALLED');
  } else if (zone.content.ticketFilter === 'WAITING') {
    tickets = tickets.filter(t => t.status === 'WAITING');
  }
  // BOTH = pas de filtre supplémentaire

  // Limite au nombre max
  const maxTickets = zone.content.maxTickets || 10;
  return tickets.slice(0, maxTickets);
};

const getQueueName = (queueId) => {
  const queue = queues.value.find(q => q._id === queueId);
  return queue ? queue.name : '';
};

const getBackgroundStyle = () => {
  if (config.value.branding?.backgroundType === 'gradient' && config.value.branding?.gradient) {
    return {
      background: `linear-gradient(${config.value.branding.gradient.direction}, ${config.value.branding.gradient.color1}, ${config.value.branding.gradient.color2})`
    };
  } else if (config.value.branding?.backgroundType === 'image' && config.value.branding?.backgroundImage) {
    return {
      backgroundImage: `url(${config.value.branding.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    };
  }
  return {
    backgroundColor: config.value.branding?.backgroundColor || '#F8FAFC'
  };
};

const getLayoutClass = () => {
  const layouts = {
    'single': 'flex-col',
    'split_horizontal': 'flex-row',
    'split_vertical': 'flex-col',
    'grid_2x2': 'grid grid-cols-2 grid-rows-2'
  };
  return layouts[config.value.layout?.type] || 'flex-col';
};

const getLogoPositionClass = () => {
  const positions = {
    'top-left': 'top-0 left-0',
    'top-center': 'top-0 left-1/2 -translate-x-1/2',
    'top-right': 'top-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-0 right-0'
  };
  return positions[config.value.branding?.logo?.position] || 'top-0 left-0';
};

const getZoneStyle = (zone) => {
  const style = {
    borderRadius: (zone.style?.borderRadius || 0) + 'px'
  };

  // Applique le fond selon le type
  if (zone.style?.backgroundType === 'color') {
    style.backgroundColor = zone.style?.backgroundColor;
  } else if (zone.style?.backgroundType === 'gradient' && zone.style?.gradient) {
    style.background = `linear-gradient(${zone.style.gradient.direction || 'to right'}, ${zone.style.gradient.color1 || '#3B82F6'}, ${zone.style.gradient.color2 || '#10B981'})`;
  } else if (zone.style?.backgroundType === 'image' && zone.style?.backgroundImage) {
    style.backgroundImage = `url(${zone.style.backgroundImage})`;
    style.backgroundSize = 'cover';
    style.backgroundPosition = 'center';
  }

  // Layout automatique ou custom
  if (config.value.layout?.type === 'custom') {
    // Mode libre : position absolue
    style.position = 'absolute';
    style.left = (zone.position?.x || 0) + '%';
    style.top = (zone.position?.y || 0) + '%';
    style.width = (zone.position?.width || 50) + '%';
    style.height = (zone.position?.height || 50) + '%';
  } else if (config.value.layout?.type === 'single') {
    style.flex = '1';
  } else if (config.value.layout?.type === 'split_horizontal' || config.value.layout?.type === 'split_vertical') {
    style.flex = '1';
  }

  return style;
};

const getTextStyle = (zone) => {
  return {
    fontFamily: zone.textStyle?.fontFamily || 'Plus Jakarta Sans',
    fontSize: (zone.textStyle?.fontSize || 16) + 'px',
    fontWeight: zone.textStyle?.fontWeight || 'normal',
    color: zone.textStyle?.color || '#1E293B',
    textAlign: zone.textStyle?.textAlign || 'left'
  };
};
</script>

<style scoped>
.font-sans { font-family: 'Plus Jakarta Sans', sans-serif; }
</style>
